<script setup lang="ts">
import { PaginationComponent } from '@metapic-apps/shared'
import { computed, toRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useGetAffiliateLinks } from '@/api/affiliate-links.api'
import LinkIcon from '@/assets/icons/link.svg?component'
import ConvertLinkModal from '@/components/CreatorDetail/ConvertLinkModal.vue'
import CreatorAffiliateLinkDetails from '@/components/CreatorDetail/CreatorAffiliateLinkDetails.vue'
import CreatorAffiliateLinkItem from '@/components/CreatorDetail/CreatorAffiliateLinkItem.vue'
import type { AffiliateLink } from '@/lib/adapters/affiliate-links.adapter'
import { BoxComponent, ButtonComponent, FlexComponent, LoadingComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  userId: number
}>()

const route = useRoute()
const router = useRouter()

const userIdRef = toRef(props, 'userId')
const { data: links, total, totalPages, page, isLoading, refetch } = useGetAffiliateLinks(userIdRef)

const selectedLinkId = computed(() => (route.query.linkId ? Number(route.query.linkId) : undefined))

const isDrawerOpen = computed({
  get: () => !!selectedLinkId.value,
  set: (open) => {
    if (open) {
      return
    }
    void router.push({ query: { ...route.query, linkId: undefined, linkTab: undefined } })
  },
})

const isConvertModalOpen = computed({
  get: () => Boolean(route.query.convertLink),
  set: (open) => {
    void router.push({ query: { ...route.query, convertLink: open ? '1' : undefined } })
  },
})

const openDrawer = (link: AffiliateLink) => {
  void router.push({ query: { ...route.query, linkId: String(link.id), linkTab: undefined } })
}

const isEmpty = computed(() => !isLoading.value && links.value.length === 0)
</script>

<template>
  <BoxComponent :p="24">
    <FlexComponent direction="column" :gap="24">
      <FlexComponent justify="space-between" align="center" :gap="16">
        <TypographyComponent variant="title-medium">{{ total }} links created</TypographyComponent>
        <ButtonComponent variant="primary" @click="isConvertModalOpen = true">
          <template #iconLeft>
            <ElIcon color="var(--inverted)" class="icon">
              <LinkIcon />
            </ElIcon>
          </template>
          Convert link
        </ButtonComponent>
      </FlexComponent>

      <LoadingComponent v-if="isLoading && links.length === 0" :size="48" />
      <TypographyComponent v-else-if="isEmpty" variant="body-large" color="var(--secondary-48)">
        No affiliate links found.
      </TypographyComponent>
      <template v-else>
        <FlexComponent direction="column" :gap="8">
          <CreatorAffiliateLinkItem v-for="link in links" :key="link.id" :link="link" @click="openDrawer(link)" />
        </FlexComponent>

        <PaginationComponent v-model:current-page="page" :total-pages="totalPages" />
      </template>
    </FlexComponent>
  </BoxComponent>

  <CreatorAffiliateLinkDetails v-if="selectedLinkId" v-model="isDrawerOpen" :link-id="selectedLinkId" @saved="refetch()" />

  <ConvertLinkModal v-if="isConvertModalOpen" v-model="isConvertModalOpen" :user-id="userId" @converted="refetch()" />
</template>
